
	import { Component, Vue, Prop, Watch } from "vue-property-decorator";
	import { namespace, Action, Getter } from "vuex-class";
	import { SidebarMenu } from "vue-sidebar-menu";
	import VuePerfectScrollbar from "vue-perfect-scrollbar";

	// SV components
	import NavHeader from "@/components/NavHeader.vue";
	import { PrefType, Pref, IconSet } from "../store/settings/types";
	import { FeaturesList } from "@/store/types";
	import { SiteTreeNode, Group } from "@/store/eventqueue/types";
	import AreaTreePicker from "@/components/form/AreaTreePicker.vue";
	import { AreaNode } from "@/types/groups/AreaNode";

	// APIs
	import api from "@/services/api.service";

	// Configuration pages
	import FileStoreSettings from "@/components/settings/filestores/FileStoreSettings.vue";
	import RegionSettings from "@/components/settings/regions/RegionSettings.vue";
	import DeviceTypeSettings from "@/components/settings/device-types/DeviceTypeSettings.vue";

	import { get } from "lodash";

	const SettingsStore = namespace("settings");
	const Eventqueue = namespace("eventqueue");

	/**
	 * Component which renders a separator in the sidebar.
	 */
	const SidebarSeparator = {
		render: function(createElement) {
			return createElement("hr", {
				style: {
					"border-color": "rgba(255,255,255,0.35)",
					margin: "20px"
				}
			});
		}
	};

	@Component({
		components: {
			VuePerfectScrollbar,
			"nav-header": NavHeader,
			"sidebar-menu": SidebarMenu,
            "area-tree-picker": AreaTreePicker
		}
	})
	export default class Settings extends Vue {
		// -- Vuex -- //
		@Getter("getFeaturesList") featuresList: FeaturesList;

		@SettingsStore.Action loadAllPrefTypes;
		@SettingsStore.Action loadAllIcons;
		@SettingsStore.Action loadAllPrefs;
		@SettingsStore.Action updatePref: (pref: Pref) => Promise<void>;
		@SettingsStore.Action createPref: (pref: Pref) => Promise<void>;
		@SettingsStore.Getter("getAllPrefTypes") allPrefTypes: PrefType[];
		@SettingsStore.Getter("getAllIcons") allIcons: IconSet[];
		@SettingsStore.Getter("getAllPrefs") allPrefs: Pref[];
		@SettingsStore.Mutation setSelectedGroup;

		private selectedCategory: any = {};
		private selectedGroupId: number = null;
		private selectedPath: Group[] = [];
		private availableSettings: PrefType[] = [];
		private selectedGroup: Group = {};
		private searchQuery: string = "";
		private lastSelectedCategory: any = null;
		private isToggleCollapsed = false;
		private isModalShown = false;
		private showSaveButton: boolean = true;
		private updatedPrefs = [];
		private parentCategories = [];
		private displayAreas: boolean = true;

		private systemArea: SiteTreeNode = {
			group: {
				groupID: 0,
				parentGroupID: null,
				title: "System Area",
				timeZoneID: 0,
				status: 0,
				selected: true
			},
			subGroups: null,
			isOpen: false
		};

		/**
		 * Pages which use separate components to configure parts of the system that do not use prefs.
		 * Note: if page has a parent, then the page will be placed as a child of that parent.
		 */
		private configurationPages = [
			{
				title: "Filestores",
				icon: "fas fa-archive",
				isConfigPage: true,
				configComponent: FileStoreSettings,
				hideSelectedPath: true,
				parent: null
			},
			{
				title: "Regions",
				icon: "fas fa-map-marker-alt",
				isConfigPage: true,
				configComponent: RegionSettings,
				hideSelectedPath: true,
				parent: "Database Federation"
			},
			{
				title: "Device Types",
				icon: "fas fa-archive",
				isConfigPage: true,
				configComponent: DeviceTypeSettings,
				hideSelectedPath: true,
				parent: null,
				areaApplicable: false
			}
		];

		// -- Computed Properties -- //
		public get currentSettings() {
			const settings: PrefType[] = [];
			this.availableSettings.forEach(setting => {
				if (
					setting.category == this.selectedCategory.title &&
					(setting.parent == this.selectedCategory.parent || setting.parent == setting.category) &&
					!setting.hidden &&
					((setting.systemOnly && this.selectedGroupId === 0) ||
						(this.selectedGroupId > 0 && !setting.systemOnly))
				) {
					setting.value = this.getPrefValue(setting.prefTypeID, this.selectedGroup);
					settings.push(setting);
				}
			});

			this.updatedPrefs = settings;
			return settings;
		}

		public get currentPrefTypes() {
			return this.allPrefTypes.filter(
				p =>
					p.title.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
					p.parent?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
					p.category?.toLowerCase().includes(this.searchQuery.toLowerCase())
			);
		}

		/**
		 * Gets the list of configuration pages, filtered by the current search query.
		 */
		public get currentConfigPages() {
			return this.configurationPages.filter(page =>
				page.title.toLowerCase().includes(this.searchQuery.toLowerCase())
			);
		}

		public get isControlCenterSelected() {
			return this.selectedGroup.parentGroupID < 1;
		}

		public get groupPath() {
			return this.selectedPath;
		}

		// -- Watchers -- //
		@Watch("selectedGroupId")
		public async currentGroup(newValue) {
			if (newValue === 0) {
				this.selectedGroup = this.systemArea.group;
				return;
			}

			this.selectedPath = [];

			if (newValue.group == null) {
				this.selectedGroup = await this.getGroup(newValue.group ? newValue.group : newValue);
			}

			let g = this.selectedGroup;
			this.selectedPath.push(this.selectedGroup);

			if (g && g.parentGroupID) {
				while (g.parentGroupID != null) {
					const parent = await this.getGroup(g.parentGroupID);
					this.selectedPath.push(parent);
					g = parent;
				}
			}

			this.selectedPath = this.selectedPath.reverse();
		}

		@Watch("searchQuery")
		public generateMenu() {
			let categories = [];
			let fullMenu = [];
			this.availableSettings = [];
			for (let prefType of this.currentPrefTypes) {
				if (prefType.hidden == true) continue;
				let exists = false;
				fullMenu.forEach(parent => {
					if (prefType.parent == parent.title) {
						this.availableSettings.push(prefType);
						let childExists = false;
						parent.child.forEach(child => {
							if (child.title == prefType.category) {
								childExists = true;
							}
						});
						if (!childExists) {
							if (prefType.category != null) {
								parent.child.push({
									title: prefType.category,
									icon: this.getIcon(prefType.category),
									child: null,
									parent: prefType.parent,
									class: this.checkSelectedNode(
										prefType.category,
										this.getIcon(prefType.category),
										prefType.parent
									)
										? "selectedNode"
										: null
								});
							}
						}
						exists = true;
					}
				});

				if (!exists) {
					if (prefType.parent != null) {
						fullMenu.push({
							title: prefType.parent,
							icon: this.getIcon(prefType.parent),
							child: [
								{
									title: prefType.category,
									icon: this.getIcon(prefType.category),
									child: null,
									parent: prefType.parent,
									class: this.checkSelectedNode(
										prefType.category,
										this.getIcon(prefType.category),
										prefType.parent
									)
										? "selectedNode"
										: null
								}
							]
						});
						this.availableSettings.push(prefType);
					}
				}
			}

			fullMenu.forEach(menu => {
				menu.child = menu.child.sort(function(a, b) {
					const textA = a.title.toUpperCase();
					const textB = b.title.toUpperCase();
					return textA < textB ? -1 : textA > textB ? 1 : 0;
				});
			});

			this.parentCategories = fullMenu.sort(function(a, b) {
				const textA = a.title.toUpperCase();
				const textB = b.title.toUpperCase();
				return textA < textB ? -1 : textA > textB ? 1 : 0;
			});

			if (this.currentConfigPages.length > 0) {
				// Add a sidebar separator
				this.parentCategories.push({
					component: SidebarSeparator
				});

				// Add configuration header to the menu
				this.parentCategories.push({
					header: true,
					title: "Configuration",
					hiddenOnCollapse: true
				});

				// Finally, add all the config pages.
				this.currentConfigPages.forEach(page => {
					// If the page has a parent, find it and push the page as its child.
					// Otherwise treat it as a parent.
					if (page.parent != null) {
						let index = this.parentCategories.findIndex(x => x.title == page.parent);
						if (index >= 0) {
							this.parentCategories[index].child.push({ ...page });
						}
					} else {
						this.parentCategories.push({ ...page });
					}
				});
			}
		}

		// -- LifeCycle Hooks -- //
		public async mounted() {
			await this.loadAllPrefTypes();
			await this.loadAllIcons();
			await this.loadAllPrefs();
			this.generateMenu();
		}

		// -- Methods -- //

		public async setGroupFromPath(group: Group): Promise<void> {
			this.setSelectedGroup(await this.getGroup(group.groupID));
			this.selectedGroupId = group.groupID;
		}

		public async setGroup(newGroup: AreaNode | null): Promise<void> {
			const selectedGroup: Group = await this.getGroup(newGroup?.id ?? 0);

			this.setSelectedGroup(selectedGroup);
			this.selectedGroupId = selectedGroup.groupID;
		}

		public async getGroup(groupID: number): Promise<Group> {
			if (groupID === 0) {
				// System Area
				return {
					groupID: groupID,
					parentGroupID: null,
					selected: true,
					title: "System Area" } as Group;
			} else {
				return await api.loadGroup(groupID);
			}
		}

		public getPrefValue(typeId, group) {
			let foundPref = this.allPrefs.filter(pref => pref.prefTypeID == typeId && pref.groupID == group?.groupID)[0];

			if (!foundPref && !group) {
				return "";
			}

			if (!foundPref) {
				return this.getPrefValue(typeId, group.parentGroupID);
			}

			const prefType = this.allPrefTypes.filter(pType => pType.prefTypeID == typeId)[0];

			switch (prefType.dataType) {
				case "S":
					return foundPref.value;
				case "B":
					switch (foundPref.value.toLowerCase()) {
						case "true":
						case "1":
							return true;
						default:
							return false;
					}
				case "I":
					return parseInt(foundPref.value);
			}
		}

		public isFeatureEnabled(feature) {
			if (!feature) return true;
			return get(this.featuresList, feature.split("/"));
		}

		public getIcon(title) {
			for (let icon of this.allIcons) {
				if (icon.title == title) {
					return icon.iconData;
				}
			}
		}

		public checkSelectedNode(title, icon, parent) {
			if (
				this.lastSelectedCategory?.item?.title == title &&
				this.lastSelectedCategory?.item?.icon == icon &&
				this.lastSelectedCategory?.item?.parent == parent
			) {
				return true;
			}

			return false;
		}

		public async save() {
			await this.currentSettings.forEach(async setting => {
				const prefSearch = this.allPrefs.filter(
					p =>
						p.prefTypeID == setting.prefTypeID &&
						(p.groupID == this.selectedGroupId ||
							(setting.systemOnly && this.selectedGroupId == this.systemArea.group.groupID))
				);

				if (
					prefSearch.length > 0 &&
					setting.value != prefSearch[0].value &&
					setting.value.toString() != prefSearch[0].value.toString()
				) {
					const updatePref: Pref = {
						groupID: prefSearch[0].groupID,
						value: setting.value,
						prefTypeID: prefSearch[0].prefTypeID,
						prefID: prefSearch[0].prefID
					};
					await this.updatePref(updatePref);
					await this.loadAllPrefs();
				} else if (prefSearch.length < 1 && (setting.value != "" || setting.value === false)) {
					const createPref: Pref = {
						prefID: 0,
						groupID: this.selectedGroupId,
						value: setting.value,
						prefTypeID: setting.prefTypeID
					};
					await this.createPref(createPref);
					await this.loadAllPrefs();
				}
			});
		}

		public async onItemClick(event, item, node) {
			// Clear the background colour of the previously selected category.
			if (this.lastSelectedCategory != null && (node.level == 2 || item.isConfigPage)) {
				this.lastSelectedCategory.$el.style.backgroundColor = null;
				this.lastSelectedCategory.$el.children[0].style.backgroundColor = null;
			}

			// If we've selected a child settings page, or a config page, update our currently selected categories.
			if (node.level == 2 || item.isConfigPage) {
				this.lastSelectedCategory = node;
				this.selectedCategory = item;

				if (item.areaApplicable == false) {
					this.displayAreas = false;
				} else {
					this.displayAreas = true;
				}
			}
			
			// If we've selected a child settings page, update it's background colour and save button visibility.
			if (node.level == 2 && !item.isConfigPage) {
				node.$el.style.backgroundColor = "#282828";
				this.showSaveButton = true;
			}
			else if (item.isConfigPage) {
				node.$el.style.backgroundColor = "#2196f3";
				this.showSaveButton = false;
			}
			
			this.generateMenu();
		}

		public onToggleCollapse(collapsed) {
			this.isToggleCollapsed = collapsed;
		}

		public showDisabledModal() {
			this.isModalShown = true;
		}

		public async switchToSystemLevel(): Promise<void> {
			this.setSelectedGroup(await this.getGroup(0));
			this.selectedGroupId = 0;
			this.hideDialog();
		}

		public hideDialog() {
			this.isModalShown = false;
		}
	}
